import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { PrioritizedToggleCardProps } from '@/types/predefined-messages';

import { PriorityIcon } from '@/components/common/priority-icon';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

export function PrioritizedToggleCard({
  checked,
  onCheckedChange,
  disabled = false,
}: PrioritizedToggleCardProps) {
  const { t } = useTranslation();
  const switchId = useId();

  const isActive = !disabled && checked;

  return (
    <div
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-md border px-4 py-3 transition-colors',
        isActive
          ? 'border-warning-fill-solid bg-warning-fill-subtle'
          : 'border-neutral-stroke bg-neutral-fill-subtle'
      )}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <PriorityIcon
          className={cn(
            isActive
              ? 'text-black [--priority-icon-border:var(--white)] [--priority-icon-mark:var(--white)]'
              : 'text-priority-disabled-fill [--priority-icon-border:var(--priority-disabled-border)] [--priority-icon-mark:var(--priority-disabled-mark)]'
          )}
        />
        <label
          htmlFor={switchId}
          className={cn(
            'truncate text-sm leading-5 font-semibold',
            isActive ? 'text-foreground' : 'text-muted-foreground',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
        >
          {t('common:prioritizedMessage.toggleLabel')}
        </label>
      </div>

      <Switch
        id={switchId}
        size="md"
        checked={isActive}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        className="data-[state=checked]:bg-success data-[state=unchecked]:bg-switch-unchecked dark:data-[state=unchecked]:bg-switch-unchecked data-[state=unchecked]:opacity-50"
        thumbClassName="bg-white dark:data-[state=checked]:bg-white dark:data-[state=unchecked]:bg-white"
      />
    </div>
  );
}
